import { AfterViewChecked, Directive, ElementRef, HostListener } from '@angular/core';
import { syncButtonNameFromParent } from './parent-name-source';

// WeakMap stores a lock callback keyed by the native event, allowing handlers
// to call lockClick(event, promise) after the directive lets the click propagate.
const eventLockMap = new WeakMap<Event, (promise: Promise<any>) => void>();

/**
 * Lock a button's click for the duration of a promise.
 * Call from any (click) handler on a cs-button / cs-icon-button / cs-bar-button
 * or any element with a cs-capture-cover button inside.
 *
 * If the event didn't come through a capture-cover button, this is a no-op.
 */
export function lockClick(event: Event, promise: Promise<any>): void {
  eventLockMap.get(event)?.(promise);
}

// cooldown for sync handlers (ms)
const SYNC_COOLDOWN_MS = 400;
// safety timeout for async handlers that never resolve (ms)
const ASYNC_SAFETY_TIMEOUT_MS = 30_000;

@Directive({
  selector: 'button[cs-capture-cover]',
  standalone: false,
})
export class CsCaptureCoverDirective implements AfterViewChecked {

  private locked = false;

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  ngAfterViewChecked(): void {
    // name the empty button from the row it covers, unless something else already names it
    syncButtonNameFromParent(this.elementRef.nativeElement);
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    // if locked, swallow the click entirely
    if (this.locked) {
      event.stopImmediatePropagation();
      event.preventDefault();
      return;
    }

    // lock immediately
    this.locked = true;

    // store a lock callback so handlers can call lockClick(event, promise)
    let lockPromise: Promise<any> | null = null;
    eventLockMap.set(event, (promise) => { lockPromise = promise; });

    // let the event propagate naturally to (click) handlers:
    // - same-element handlers (internal components) fire after @HostListener
    // - ancestor handlers (cs-button host) fire during event bubbling
    // use setTimeout(0) instead of queueMicrotask because microtasks run at the
    // checkpoint after each handler returns (before bubbling continues), while
    // setTimeout(0) runs after the entire event dispatch completes

    setTimeout(() => {
      eventLockMap.delete(event);

      if (lockPromise) {
        // async-aware lock: unlock when promise settles
        const safetyTimer = setTimeout(() => {
          this.locked = false;
        }, ASYNC_SAFETY_TIMEOUT_MS);

        void lockPromise.finally(() => {
          clearTimeout(safetyTimer);
          // keep lock for a cooldown after async completion — the DOM may still
          // be interactive during page-transition animations
          setTimeout(() => {
            this.locked = false;
          }, SYNC_COOLDOWN_MS);
        });
      } else {
        // sync handler or no handler: cooldown-based unlock
        setTimeout(() => {
          this.locked = false;
        }, SYNC_COOLDOWN_MS);
      }
    });
  }
}
